import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED,
  TestSetupModule
} from '@nrwl/test-setup';
import { CommonLibModule, CommonRegEx, Debounce, Device, DevicesService } from '@nrwl/common-lib';
import {
  Y1731ConfigFieldMapping as ConfigFieldMapping,
  CONFIRM_SELECTION,
  ETHER_TYPE,
  REPORT_INTERVAL_TYPES
} from '../../models/models';
import { Y1731SetupService } from '../../y1731-setup.service';
import { Subject, Subscription } from 'rxjs';
import { CommonMaterialModule } from '@nrwl/common-material';
import { isNil } from 'lodash';

@Component({
  selector: 'nrwl-y1731-setup',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './y1731-setup.component.html',
  styleUrls: ['./y1731-setup.component.scss']
})
export class Y1731SetupComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('setupForm', { static: true }) form: NgForm;
  configMapping = ConfigFieldMapping;
  newTestMode = true;
  customerName = '';
  technicianName = '';
  contact = '';
  email = '';
  phone = '';
  technicianId = '';
  comments='';
  testName = 'Y1731';
  editMode = false;
  cloneMode = false;
  macRegex = CommonRegEx.MAC;
  testInfo: any;
  config: any;
  outputs: Array<string>;
  tabsubscription: Subscription;
  selectedOriginator: Device;
  actualTestName: string;
  agentType = null;
  pmReportIntervalTypes = REPORT_INTERVAL_TYPES;
  persistAfterRebootSelection = CONFIRM_SELECTION;
  etherTypesData = ETHER_TYPE;
  vlanConfigTypes = [];
  formField = {
    srcMacAddress: null,
    mepId: null,
    pmReportInterval: null,
    persistAfterReboot: null,
    vlanConfig: null,
    svlan: null,
    svlanPriority: null,
    cvlan: null,
    cvlanPriority: null,
    cvlanEtherType: null
  };
  readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    public setupService: Y1731SetupService,
    private readonly deviceService: DevicesService
  ) {}

  ngOnInit(): void {
    this.tab.form = this.form;
    this.initializeData();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 0) {
          this.restoreFormValues();
        }
      });
    }
  }

  initializeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.setupService.currentTab.config;
    this.outputs = this.config.def.outputs;
    this.actualTestName = this.testName;
    this.vlanConfigTypes = this.config.options[ConfigFieldMapping.VLAN_CONFIG].value;
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.customerName = this.restoreFieldValues(ConfigFieldMapping.CUSTOMER);
    this.technicianName = this.restoreFieldValues(ConfigFieldMapping.TECHINICIAN);
    this.contact = this.restoreFieldValues(ConfigFieldMapping.CONTACT);
    this.email = this.restoreFieldValues(ConfigFieldMapping.EMAIL);
    this.phone = this.restoreFieldValues(ConfigFieldMapping.PHONE);
    this.technicianId = this.restoreFieldValues(ConfigFieldMapping.TECHINICIANID);
    this.comments = this.restoreFieldValues(ConfigFieldMapping.COMMENTS);
    this.agentType = this.restoreFieldValues(ConfigFieldMapping.AGENT_TYPE);
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.formField.srcMacAddress = this.restoreFieldValues(ConfigFieldMapping.SRC_MAC_ADDRESS);
    this.formField.mepId = this.restoreFieldValues(ConfigFieldMapping.MEP_ID);
    this.formField.pmReportInterval = this.restoreFieldValues(
      ConfigFieldMapping.PM_REPORT_INTERVAL
    );
    this.formField.persistAfterReboot = this.restoreFieldValues(
      ConfigFieldMapping.PERSIST_AFTER_REBOOT
    );
    this.formField.vlanConfig = this.restoreFieldValues(ConfigFieldMapping.VLAN_CONFIG);
    this.formField.svlan = this.restoreFieldValues(ConfigFieldMapping.SVLAN_ID);
    this.formField.cvlan = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ID);
    this.formField.svlanPriority = this.restoreFieldValues(ConfigFieldMapping.SVLAN_PRIORITY);
    this.formField.cvlanPriority = this.restoreFieldValues(ConfigFieldMapping.CVLAN_PRIORITY);
    this.formField.cvlanEtherType = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ETHER_TYPE);
  }

  updateTestIdentity(event: {
    testName: string;
    customer: string;
    technician: string;
    contact: string;
    email: string;
    phone: string;
    technicianId: string;
    isValidName: boolean;
    comments: string;
  }): void {
    this.onDataEmit('alias', event.testName, event.isValidName);
    this.onDataEmit('customer', event.customer, true);
    this.onDataEmit('technician', event.technician, true);
    this.onDataEmit('contact', event.contact, true);
    this.onDataEmit('email', event.email, true);
    this.onDataEmit('phone', event.phone, true);
    this.onDataEmit('technicianId', event.technicianId, true);
    this.onDataEmit('comments',event.comments,true);
    const controls = this.form?.form?.controls;
    const allValid = controls ? Object.values(controls).every(control => !control.invalid) : true;
    const isFormValid = event.isValidName && allValid;
    this.form.form?.setErrors(isFormValid ? null : { invalid: true });
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  // eslint-disable-next-line
  onAgentChange(data: any): void {
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    this.setOriginator();
  }

  setOriginator(): void {
    this.agentType = this.selectedOriginator.getModel();
    this.onDataEmit(ConfigFieldMapping.AGENT_TYPE, this.agentType, true);
    this.setupService.setOriginatorAgent(this.selectedOriginator);
    this.onDataEmit(ConfigFieldMapping.SELECTED_ORIGINATOR, this.selectedOriginator, true);
    this.restoreAgentBasedConfig(this.agentType);
    this.restoreFormValues();
    this.getSrcMacAddressFromSourceAgent();
  }

  getSrcMacAddressFromSourceAgent(): void {
    if (this.selectedOriginator) {
      this.setupService.setOriginatorAgent(this.selectedOriginator);
      const capabilityData = this.setupService.getAgentLookUpData(
        this.config.lookup,
        this.selectedOriginator
      );
      this.formField.srcMacAddress = capabilityData[ConfigFieldMapping.SRC_MAC_ADDRESS];
      this.onSourceMacAddressChange();
    }
  }

  @Debounce(500)
  onSourceMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_MAC_ADDRESS, this.formField.srcMacAddress, true);
  }

  onMepIdInputChange(): void {
    this.onDataEmit(ConfigFieldMapping.MEP_ID, this.formField.mepId, true);
  }

  onPmReportIntervalChange(): void {
    this.onDataEmit(ConfigFieldMapping.PM_REPORT_INTERVAL, this.formField.pmReportInterval, true);
  }

  onPersistAfterRebootChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.PERSIST_AFTER_REBOOT,
      this.formField.persistAfterReboot,
      true
    );
  }

  validityCheck(data: number, min: number, max: number): boolean {
    if (data === 0) {
      return true;
    }
    if (data) {
      return !(data <= max && data >= min);
    }
  }
  vlanUpdated(): void {
    let valid = false;
    if (!this.validityCheck(this.formField.svlan, 1, 4095)) {
      this.formField.svlanPriority = 0;
      valid = true;
    }
    this.onDataEmit(ConfigFieldMapping.SVLAN_ID, this.formField.svlan, valid);
  }
  vlanPriorityUpdated(): void {
    if (!this.formField.svlanPriority) {
      this.formField.svlanPriority = 0;
    }
    const isValid = this.formField.svlanPriority >= 0 && this.formField.svlanPriority <= 7;
    this.onDataEmit(ConfigFieldMapping.SVLAN_PRIORITY, this.formField.svlanPriority, isValid);
  }

  onVlanConfigChange(): void {
    this.onDataEmit(ConfigFieldMapping.VLAN_CONFIG, this.formField.vlanConfig, true);
    this.formField.svlan = '';
    this.formField.cvlan = '';
    this.onDataEmit(ConfigFieldMapping.SVLAN_ID, this.formField.svlan, true);
    this.onDataEmit(ConfigFieldMapping.CVLAN_ID, this.formField.cvlan, true);
  }

  onSVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SVLAN_ID,
      this.formField.svlan,
      this.form.controls['sVlanInput']?.valid
    );
  }

  onSVlanPriorityChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SVLAN_PRIORITY,
      this.formField.svlanPriority,
      this.form.controls['sVlanPriorityInput']?.valid
    );
  }
  onCVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CVLAN_ID,
      this.formField.cvlan,
      this.form.controls['cVlanInput']?.valid
    );
  }

  onCVlanPriorityChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CVLAN_PRIORITY,
      this.formField.svlanPriority,
      this.form.controls['cVlanPriority']?.valid
    );
  }

  onCVlanEtherTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.CVLAN_ETHER_TYPE, this.formField.cvlanEtherType, true);
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }
}
